import React, { useEffect, useState } from 'react';
import { gatewayApi } from '../services/gateway';
import SafeIcon, { HelpHint } from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { 
  FiFileText, FiPlus, FiEdit2, FiCopy, FiTrash2, FiEye, FiCheck, FiX, 
  FiCode, FiType, FiMail, FiSmartphone, FiLayout, FiInfo, FiSend
} = FiIcons;
const asTemplate = (t) => ({id:t.id,key:t.template_key,name:t.template_name,subject:t.subject,htmlBody:t.html_body,textBody:t.text_body||'',status:t.status,allowedApps:t.app_id?[t.app_id]:[],clientId:t.client_id||''});

const TemplateModal = ({ template, onSave, onClose, apps }) => {
  const [formData, setFormData] = useState(template || {
    key: '', name: '', subject: '', fromName: '', fromEmail: '', 
    replyTo: '', htmlBody: '', status: 'active', allowedApps: []
  });
  const [activeTab, setActiveTab] = useState('editor'); // 'editor', 'preview'

  const variables = [
    '{{name}}', '{{businessName}}', '{{email}}', '{{phone}}', 
    '{{message}}', '{{otp}}', '{{expiryMinutes}}', '{{resourceName}}', 
    '{{bookingDate}}', '{{bookingTime}}', '{{appName}}'
  ];

  const handleInsertVariable = (v) => {
    setFormData({ ...formData, htmlBody: formData.htmlBody + v });
  };

  const renderPreview = () => {
    let preview = formData.htmlBody;
    variables.forEach(v => {
      const sampleVal = v.replace('{{', '').replace('}}', '').toUpperCase();
      preview = preview.replaceAll(v, `<span style="color: #2563eb; background: #eff6ff; padding: 0 4px; border-radius: 4px; font-weight: bold;">[${sampleVal}]</span>`);
    });
    return preview;
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2">
            <SafeIcon icon={template ? FiEdit2 : FiPlus} className="text-blue-400" />
            {template ? 'Edit Email Template' : 'Create New Template'}
          </h3>
          <div className="flex bg-slate-800 p-1 rounded-lg">
            <button onClick={() => setActiveTab('editor')} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${activeTab === 'editor' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}>Editor</button>
            <button onClick={() => setActiveTab('preview')} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${activeTab === 'preview' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}>Preview</button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {activeTab === 'editor' ? (
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Template Key (Read Only)</label><HelpHint label={"Template Key (Read Only)"}/>
                  <input 
                    type="text" 
                    placeholder="e.g. contact_form_alert"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono outline-none"
                    value={formData.key}
                    onChange={(e) => setFormData({...formData, key: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Display Name</label><HelpHint label={"Display Name"}/>
                  <input 
                    type="text" 
                    placeholder="e.g. Contact Form Admin Alert"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-blue-500"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Subject Line</label><HelpHint label={"Subject Line"}/>
                <input 
                  type="text" 
                  placeholder="New Message from {{name}}"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white focus:border-blue-500"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                />
              </div>

              <p className="text-xs text-slate-500 rounded-lg bg-slate-50 p-3">Sender name, From address and Reply-To are configured centrally for the application or API key, not inside an email template.</p>

              <div className="space-y-1.5 flex flex-col h-[300px]">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">HTML Body</label><HelpHint label={"HTML Body"}/>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {variables.map(v => (
                    <button 
                      key={v} 
                      onClick={() => handleInsertVariable(v)}
                      className="px-2 py-1 bg-blue-50 text-blue-600 text-[10px] font-bold rounded border border-blue-100 hover:bg-blue-100 transition-colors"
                    >
                      {v}
                    </button>
                  ))}
                </div>
                <textarea 
                  className="w-full flex-1 p-4 bg-slate-900 text-emerald-400 font-mono text-xs rounded-xl outline-none resize-none"
                  value={formData.htmlBody}
                  onChange={(e) => setFormData({...formData, htmlBody: e.target.value})}
                ></textarea>
              </div>
            </div>
          ) : (
            <div className="flex-1 bg-slate-100 p-8 overflow-y-auto flex flex-col items-center">
              <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200">
                <div className="p-6 border-b border-slate-100">
                  <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-2">Email Preview</p>
                  <h4 className="text-lg font-bold text-slate-800">{formData.subject || 'No Subject'}</h4>
                  <div className="flex gap-4 mt-2 text-xs">
                    <span className="text-slate-500">From: <b className="text-slate-700">Configured application sender</b></span>
                  </div>
                </div>
                <iframe title="Isolated email template preview" sandbox="" referrerPolicy="no-referrer" srcDoc={renderPreview()} className="w-full min-h-[320px] border-0" />
                <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-400 text-center">
                  This is a simulated preview of how the email will look to recipients.
                </div>
              </div>
            </div>
          )}

          <div className="w-64 border-l border-slate-100 p-6 space-y-6 bg-slate-50/50">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</label><HelpHint label={"Status"}/>
              <select className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                <option value="active">Active</option>
                <option value="draft">Draft</option><option value="archived">Archived</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Allowed Apps</label><HelpHint label={"Allowed Apps"}/>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input name="template-app" type="radio" checked={!formData.allowedApps.length}
                    onChange={() => setFormData({...formData,allowedApps:[]})} />
                  <span className="text-xs text-slate-600">Global (all authorised apps)</span>
                </label>
                {apps.map(app => (
                  <label key={app.id} className="flex items-center gap-2 cursor-pointer">
                    <input name="template-app" type="radio" checked={formData.allowedApps[0] === app.id}
                      onChange={() => setFormData({...formData,allowedApps:[app.id]})} />
                    <span className="text-xs text-slate-600">{app.name}</span>
                  </label>
                ))}              </div>
            </div>

            <div className="pt-4 border-t border-slate-200 space-y-3">
              <button disabled className="w-full py-2.5 bg-slate-200 text-slate-500 text-xs font-bold rounded-xl cursor-not-allowed flex items-center justify-center gap-2">
                <SafeIcon icon={FiSend} /> Send Test Email
              </button>
              <p className="text-[9px] text-slate-400 italic text-center">Test sending requires a linked provider (Coming Soon).</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-2 text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
          <button onClick={() => onSave(formData)} className="px-8 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-blue-700 transition-all">Save Template</button>
        </div>
      </div>
    </div>
  );
};

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [apps, setApps] = useState([]);
  const [loadError, setLoadError] = useState('');
  useEffect(() => {
    Promise.all([gatewayApi.listTemplates(),gatewayApi.listApps()]).then(([t,a]) => {
      setTemplates((t.data || []).map(asTemplate));setApps(a.data || []);
    }).catch(err => setLoadError(err.message || 'Unable to load templates'));
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeModal, setActiveModal] = useState(null); // 'add', 'edit'
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const filteredTemplates = templates.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    t.key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Email Templates</h2>
          <p className="text-sm text-slate-500">Reusable content blocks for transactional and triggered emails.</p>
        </div>
        <button onClick={() => setActiveModal('add')} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center gap-2">
          <SafeIcon icon={FiPlus} /> Create Template
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex-1 flex items-center px-3 gap-2">
          <SafeIcon icon={FiIcons.FiSearch} className="text-slate-400" />
          <input type="text" placeholder="Search templates..." className="w-full text-sm outline-none bg-transparent" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      {loadError && <p role="alert" className="text-red-700 p-3">{loadError}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map(template => (
          <div key={template.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:border-blue-300 hover:shadow-md transition-all group overflow-hidden flex flex-col">
            <div className="p-6 flex-1">
              <div className="flex justify-between items-start mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${template.status === 'active' ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-400'}`}>
                  <SafeIcon icon={FiLayout} />
                </div>
                <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${template.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {template.status}
                </span>
              </div>
              <h3 className="font-bold text-slate-800 line-clamp-1">{template.name}</h3>
              <p className="text-[10px] font-mono text-slate-400 mt-1">{template.key}</p>
              <div className="mt-4 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-[10px] text-slate-400 font-bold uppercase mb-1 flex items-center gap-1">
                  <SafeIcon icon={FiMail} className="text-[8px]" /> Subject
                </p>
                <p className="text-xs text-slate-700 line-clamp-1 italic font-medium">{template.subject}</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex justify-between items-center">
              <div className="flex -space-x-2">
                {template.allowedApps.map(appId => (
                  <div key={appId} className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[8px] font-bold text-slate-600" title={appId}>
                    {appId.split('_')[1]}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setSelectedTemplate(template); setActiveModal('edit'); }} className="p-2 text-slate-400 hover:text-blue-600 transition-colors"><SafeIcon icon={FiEdit2} /></button>
                <button className="p-2 text-slate-400 hover:text-red-500 transition-colors"><SafeIcon icon={FiTrash2} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {activeModal && (
        <TemplateModal 
          apps={apps}
          template={selectedTemplate}
          onClose={() => { setActiveModal(null); setSelectedTemplate(null); }}
          onSave={async (data) => {
            try {
              setLoadError('');
              const payload={id:data.id,template_key:data.key,template_name:data.name,
                subject:data.subject,html_body:data.htmlBody,text_body:data.textBody||'',
                status:data.status==='inactive'?'draft':data.status,
                app_id:data.allowedApps[0]||'',client_id:data.clientId||''};
              const result=await gatewayApi.saveTemplate(payload);
              const saved=asTemplate(result.data);
              setTemplates(prev=>[...prev.filter(t=>t.id!==saved.id),saved]);
              setActiveModal(null);setSelectedTemplate(null);
            } catch (error) { setLoadError(error.message || 'Template save failed'); }
          }}
        />
      )}
    </div>
  );
};

export default Templates;